import React, { useEffect } from 'react';
import i18n from '../../../../../i18n';
import { useDrillDeck } from './DrillDeck';

export type DrillTileSpan = 3 | 4 | 6 | 8 | 12;

export function DrillTile({
  sectionId,
  title,
  meta,
  n,
  span = 6,
  inspector,
  children,
  className = '',
  strip = false,
  agent = false,
  id,
  'data-ftue': dataFtue,
}: {
  sectionId: string;
  title: string;
  meta?: React.ReactNode;
  /** The reservations or guests behind the tile, printed as its footer. */
  n?: React.ReactNode;
  span?: DrillTileSpan;
  /** Detail body shown in the right inspector when this tile is focused. */
  inspector?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** Full-width typography strip (opportunity) — not a chart tile. */
  strip?: boolean;
  /** Agent / AI highlight treatment (magenta signal). */
  agent?: boolean;
  id?: string;
  'data-ftue'?: string;
}) {
  const { setFocusId, register, unregister, isFocused, inspectorId } = useDrillDeck();
  const active = isFocused(sectionId);
  const canOpen = inspector != null;

  useEffect(() => {
    if (!canOpen) return;
    register(sectionId, title, inspector);
  }, [canOpen, sectionId, title, inspector, register]);

  useEffect(() => {
    if (!canOpen) return;
    return () => unregister(sectionId);
  }, [canOpen, sectionId, unregister]);

  const onActivate = () => {
    if (!canOpen) return;
    setFocusId(active ? null : sectionId);
  };

  const Tag = canOpen ? 'button' : 'div';
  const openHint = (
    <span className="ddrill-tile-open-hint" aria-hidden>
      {active ? i18n.t('performance:drillUi.close') : i18n.t('performance:drillUi.details')}
    </span>
  );

  return (
    <Tag
      id={id}
      type={canOpen ? 'button' : undefined}
      className={`ddrill-tile span-${span}${active ? ' is-active' : ''}${strip ? ' is-strip' : ''}${agent ? ' is-agent' : ''}${canOpen ? ' is-interactive' : ''}${className ? ` ${className}` : ''}`}
      onClick={canOpen ? onActivate : undefined}
      aria-expanded={canOpen ? active : undefined}
      aria-controls={canOpen ? inspectorId : undefined}
      aria-label={
        strip
          ? `${title}. ${active ? i18n.t('performance:drillUi.closeDetails') : i18n.t('performance:drillUi.openDetails')}`
          : undefined
      }
      data-ftue={dataFtue}
    >
      {strip ? (
        <>
          <div className="ddrill-tile-body">{children}</div>
          {canOpen ? openHint : null}
        </>
      ) : (
        <>
          <div className="ddrill-tile-head">
            <div className="ddrill-tile-head-main">
              <h3 className="ddrill-tile-title">{title}</h3>
              {meta ? <p className="ddrill-tile-meta">{meta}</p> : null}
            </div>
            {canOpen ? openHint : null}
          </div>
          <div className="ddrill-tile-body">{children}</div>
          {n ? <p className="ddrill-tile-n">{n}</p> : null}
        </>
      )}
    </Tag>
  );
}
